import { useContext, useMemo, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDownIcon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { Trans, useTranslation } from "react-i18next";
import clsx from "clsx";
import DataTableIcon from "../components/icons/DataTableIcon";
import ActivatedDataTablePanel from "./ActivatedDataTablePanel";
import DataTableCalculationRowsModal from "./DataTableCalculationRowsModal";
import { MapOverlayContext } from "./MapContextManager";
import { MapUIStateContext } from "./MapUIContext";
import useCurrentProjectMetadata from "../useCurrentProjectMetadata";

/**
 * Call-to-action footer shown at the bottom of map popups (and sidebar
 * popups) for overlay layers that have Data Tables. Improves discoverability
 * of the feature by surfacing the same table picker that the legend's table
 * button opens (ActivatedDataTablePanel), using consistent iconography.
 *
 * For Mapbox popups this is portaled into an element appended to the popup
 * content by LayerInteractivityManager (see popupContentWithDataTablesFooter).
 */
export default function DataTablesPopupFooter({
  tocStableId,
  variant = "popup",
  featureProperties,
}: {
  /** Table of contents stableId of the layer the popup describes */
  tocStableId: string;
  /** "popup" renders a full-bleed footer band for mapboxgl popups */
  variant?: "popup" | "sidebar";
  /** Properties of the clicked feature, to preselect the site in QA/QC UI */
  featureProperties?: { [name: string]: any };
}) {
  const { t } = useTranslation("homepage");
  const { tableOfContentsItems, layerStatesByTocStaticId } =
    useContext(MapOverlayContext);
  const { interactivityManager } = useContext(MapUIStateContext);
  const { data: projectMeta } = useCurrentProjectMetadata();
  const dataTablesFeatureEnabled = Boolean(
    projectMeta?.project?.featureFlags?.dataTables
  );
  const [open, setOpen] = useState(false);
  const [showCalculationRows, setShowCalculationRows] = useState(false);

  const item = useMemo(
    () => (tableOfContentsItems || []).find((i) => i.stableId === tocStableId),
    [tableOfContentsItems, tocStableId]
  );
  const tables = item?.overlayDataTables || [];
  const count = tables.length;

  if (!dataTablesFeatureEnabled || !item || count === 0) {
    return null;
  }

  const activeStableId =
    layerStatesByTocStaticId[tocStableId]?.dataTable?.stableId;
  const activeTable = tables.find((table) => table.stableId === activeStableId);
  const name = activeTable?.name;
  // Join value of the clicked feature (site), via the overlay's join
  // property. Used to preselect the site in the rows-in-calculation modal.
  const joinProperty =
    activeTable?.overlayJoinColumn || activeTable?.joinColumn;
  const clickedSite =
    joinProperty &&
    featureProperties &&
    featureProperties[joinProperty] !== undefined &&
    featureProperties[joinProperty] !== null
      ? String(featureProperties[joinProperty])
      : undefined;

  return (
    <div
      className={clsx(
        "border-t border-black border-opacity-10 bg-gray-50",
        variant === "popup"
          ? // Full-bleed band: cancels .mapboxgl-popup-content padding
            // (px-3, 15px bottom — see index.css/tailwind.css overrides).
            // Note mb-[-15px]: the -mb-[15px] form isn't generated by this
            // Tailwind build.
            "-mx-3 mb-[-15px] mt-3 rounded-b px-3 py-2.5"
          : "flex-none px-4 py-3"
      )}
    >
      <p className="mb-2 text-xs leading-snug text-gray-700">
        {activeTable ? (
          t("Now visualizing")
        ) : count === 1 ? (
          <Trans ns="homepage">
            This layer includes a data table that can be visualized on the map.
          </Trans>
        ) : (
          <Trans ns="homepage">
            This layer includes {{ count }} data tables that can be visualized
            on the map.
          </Trans>
        )}
      </p>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            aria-label={
              activeTable ? t("Change Data Table") : undefined
            }
            className="w-full inline-flex items-center justify-between gap-3 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 hover:border-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1"
          >
            <span className="inline-flex min-w-0 items-center gap-2">
              <DataTableIcon className="w-[19px] h-[14px] flex-none text-gray-500" />
              <span className="truncate">
                {activeTable
                  ? name
                  : count === 1
                  ? t("Visualize Table")
                  : t("Visualize Tables")}
              </span>
            </span>
            <ChevronDownIcon className="w-3.5 h-3.5 flex-none text-gray-400" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <ActivatedDataTablePanel
            layerId={tocStableId}
            tocItemId={item.id}
            tables={tables}
            onTableSelected={() => setOpen(false)}
            onDataTableActivated={() => {
              if (variant === "popup") {
                // Get the popup out of the way so the new visualization and
                // legend controls are immediately visible.
                interactivityManager?.closeActivePopup();
              }
            }}
          />
        </Popover.Portal>
      </Popover.Root>
      {activeTable && (
        <button
          type="button"
          onClick={() => setShowCalculationRows(true)}
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-primary-600 underline decoration-primary-600/30 underline-offset-2 transition-colors hover:text-primary-700 hover:decoration-primary-700/60"
        >
          <MagnifyingGlassIcon className="h-3.5 w-3.5 flex-none" />
          {t("Show rows in calculation")}
        </button>
      )}
      {showCalculationRows && activeTable && (
        <DataTableCalculationRowsModal
          tocStableId={tocStableId}
          initialSite={clickedSite}
          onRequestClose={() => setShowCalculationRows(false)}
        />
      )}
    </div>
  );
}
